import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShoppingCart, Sparkles, Check, Copy } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useCartStore } from "@/stores/cartStore";
import { toast } from "sonner";
import popupLogo from "@/assets/popup-logo.webp";

const DISCOUNT_CODE = "GRIP15";

interface ExitIntentPopupProps {
  /** Call trigger() from CartDrawer when user closes the drawer with items still in cart */
  triggerRef?: React.MutableRefObject<(() => void) | null>;
}

const ExitIntentPopup = ({ triggerRef }: ExitIntentPopupProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [copied, setCopied] = useState(false);

  const items = useCartStore((s) => s.items);
  const applyDiscountCode = useCartStore((s) => s.applyDiscountCode);

  // Expose trigger so CartDrawer can call it
  const trigger = useCallback(() => {
    const shown = sessionStorage.getItem("exitIntentShown");
    if (!shown) {
      setIsOpen(true);
      sessionStorage.setItem("exitIntentShown", "true");
    }
  }, []);

  if (triggerRef) {
    triggerRef.current = trigger;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return;

    setIsSubmitting(true);
    try {
      // Route through edge function for server-side validation & DB inserts
      const { error } = await supabase.functions.invoke("submit-form", {
        body: { formType: "email_subscribe", data: { email, source: "exit_intent" } },
      });

      if (error) {
        console.error("Subscribe edge function error:", error);
      }

      const result = await applyDiscountCode(DISCOUNT_CODE);
      if (result.success) {
        toast.success("15% discount auto-applied to your cart!");
      }
      setIsSubscribed(true);
    } catch (error) {
      console.error("Exit intent email save failed:", error);
      setIsSubscribed(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(DISCOUNT_CODE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="exit-intent-popup"
          className="fixed inset-0 z-[70]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsOpen(false)}
          />
          <div
            className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none"
          >
            <motion.div
              className="pointer-events-auto w-full max-w-md rounded-2xl shadow-2xl bg-card overflow-hidden relative"
              initial={{ scale: 0.9, y: -30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: -20 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              <button
                onClick={() => setIsOpen(false)}
                className="absolute right-4 top-4 p-2 rounded-full bg-muted/80 hover:bg-muted transition-colors z-10"
              >
                <X className="h-4 w-4" />
              </button>

              <div className="p-8 text-center">
                <motion.img
                  src={popupLogo}
                  alt="Grizzlee Gripz"
                  className="h-16 w-full max-w-[280px] object-contain mx-auto mb-5"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                />

                <AnimatePresence mode="wait">
                  {!isSubscribed ? (
                    <motion.div
                      key="form"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                    >
                      <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <ShoppingCart className="w-7 h-7 text-primary" />
                      </div>

                      <h2 className="font-display text-2xl font-bold mb-2">Wait! Don't Leave Yet</h2>
                      <p className="text-muted-foreground text-sm mb-1">
                        You have <span className="font-semibold text-foreground">{itemCount} item{itemCount !== 1 ? "s" : ""}</span> in your cart.
                      </p>
                      <p className="text-muted-foreground text-sm mb-5">
                        Enter your email and get <span className="font-bold text-primary">15% off</span> your order!
                      </p>

                      <form onSubmit={handleSubmit} className="space-y-3">
                        <Input
                          id="exit-intent-email"
                          name="email"
                          type="email"
                          placeholder="Enter your email address"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          className="h-12 text-center border-2 focus:border-primary"
                        />
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                          <Button
                            type="submit"
                            className="w-full h-12 font-bold gap-2 text-white hover:opacity-90"
                            style={{ backgroundColor: "#282828" }}
                            disabled={!email || !email.includes("@") || isSubmitting}
                          >
                            <Sparkles className="w-4 h-4" />
                            {isSubmitting ? "Applying..." : "Claim 15% Off"}
                          </Button>
                        </motion.div>
                      </form>

                      <button
                        onClick={() => setIsOpen(false)}
                        className="mt-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        No thanks, I'll pay full price
                      </button>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                    >
                      <motion.div
                        className="w-16 h-16 bg-gradient-to-br from-primary to-orange-400 rounded-full flex items-center justify-center mx-auto mb-4"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1, rotate: [0, 10, -10, 0] }}
                      >
                        <Check className="w-8 h-8 text-primary-foreground" />
                      </motion.div>

                      <h2 className="font-display text-2xl font-bold mb-2">You're All Set! 🎉</h2>
                      <p className="text-muted-foreground text-sm mb-4">Your 15% discount code:</p>

                      <motion.div
                        className="bg-primary/10 border-2 border-dashed border-primary rounded-xl p-4 mb-4"
                        animate={{
                          boxShadow: [
                            "0 0 15px rgba(251,146,60,0.2)",
                            "0 0 25px rgba(251,146,60,0.4)",
                            "0 0 15px rgba(251,146,60,0.2)",
                          ],
                        }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        <span className="font-display text-3xl font-bold text-primary tracking-widest">
                          {DISCOUNT_CODE}
                        </span>
                      </motion.div>

                      <Button onClick={handleCopy} variant="outline" className="w-full h-10 gap-2 border-2 mb-2">
                        {copied ? (
                          <>
                            <Check className="w-4 h-4 text-green-500" /> Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" /> Copy Code
                          </>
                        )}
                      </Button>

                      <p className="text-xs text-muted-foreground">Applied at checkout automatically</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExitIntentPopup;
